<template>
  <BaseSideModal
    class="client-changes-modal"
    :width="'1100px'"
    :show-footer="true"
    @close="$emit('close')"
  >
    <template #title>{{ $t('carriers.import.clientChanges.Title') }}</template>

    <div class="run-content">
      <p class="body-text color--dark-purple mb_8">{{ $t('carriers.import.clientChanges.Intro') }}</p>

      <!-- The run's figures, whatever the page or the search shows: how many people it touched,
           and of them how many it made, recognised by name, or corrected. -->
      <p v-if="counts" class="client-changes__counts body-text color--black-purple mb_16">
        <b>{{ $t('carriers.import.clientChanges.Clients', { count: formatCount(counts.clients) }) }}</b>
        <span v-if="counts.created"> · {{ $t('carriers.import.clientChanges.New', { count: formatCount(counts.created) }) }}</span>
        <span v-if="counts.matchedByName"> · {{ $t('carriers.import.clientChanges.ByName', { count: formatCount(counts.matchedByName) }) }}</span>
        <span v-if="counts.rfcCorrected"> · {{ $t('carriers.import.clientChanges.RfcCorrected', { count: formatCount(counts.rfcCorrected) }) }}</span>
      </p>

      <InputSearch
        class="client-changes__search mb_16"
        :value="searchQuery"
        :placeholder="$t('carriers.import.clientChanges.Search')"
        :opened="true"
        @searchUpdate="search($event)"
      />

      <p v-if="loadError" class="body-text color--red">{{ loadError }}</p>
      <p v-else-if="!loading && !items.length" class="body-text color--dark-purple">
        {{ searchQuery ? $t('carriers.import.clientChanges.NobodyMatches') : $t('carriers.import.clientChanges.Empty') }}
      </p>

      <!-- One row per client; a cell holds as many lines as the client has policies or changes. -->
      <table v-if="items.length" class="client-changes__table">
        <thead>
          <tr>
            <th>{{ $t('carriers.import.clientChanges.headers.client') }}</th>
            <th>{{ $t('carriers.import.clientChanges.headers.recognisedBy') }}</th>
            <th>{{ $t('carriers.import.clientChanges.headers.name') }}</th>
            <th>{{ $t('carriers.import.clientChanges.headers.rfc') }}</th>
            <th>{{ $t('carriers.import.clientChanges.headers.policies') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in items" :key="c.personProfileId">
            <td>
              <a
                :href="`/admin/users/${c.personProfileId}`"
                target="_blank"
                class="body-text body-text--link color--purple"
              >{{ c.clientName || c.nameAfter || c.personProfileId }}</a>
            </td>
            <td>
              <span :class="{ 'client-changes__new': c.recognisedBy === 'NEW' }">
                {{ c.recognisedBy ? $t(`carriers.import.clientChanges.recognisedBy.${c.recognisedBy}`) : '—' }}
              </span>
            </td>
            <!-- Only when the carrier prints the name differently from how Renova has it:
                 what it was, struck through, and what the carrier says. -->
            <td>
              <template v-if="nameDiffers(c)">
                <span v-if="c.nameBefore" class="run-diff--was">{{ c.nameBefore }}</span>
                <span v-if="c.nameBefore"> → </span>
                <span class="run-diff--now">{{ c.nameAfter }}</span>
              </template>
              <span v-else>—</span>
            </td>
            <td>
              <template v-if="c.rfcAfter">
                <span class="run-diff--was">{{ c.rfcBefore || '—' }}</span>
                <span> → </span>
                <span class="run-diff--now">{{ c.rfcAfter }}</span>
              </template>
              <span v-else>—</span>
            </td>
            <td>
              <p v-for="p in c.policies" :key="`${p.policyNumber}-${p.certificate || ''}`" class="client-changes__policy">
                <a
                  v-if="p.insurancePolicyId"
                  :href="`/admin/policies/${p.insurancePolicyId}`"
                  target="_blank"
                  class="body-text body-text--link color--purple"
                >{{ p.policyNumber }}</a>
                <span v-else>{{ p.policyNumber }}</span>
                <span v-if="p.certificate" class="color--dark-purple"> · {{ $t('carriers.import.clientChanges.Certificate', { n: p.certificate }) }}</span>
                <span class="color--dark-purple"> · {{ $t(`carriers.import.clientChanges.outcome.${p.outcome}`) }}</span>
              </p>
              <span v-if="!c.policies.length">—</span>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- The next page arrives when this scrolls into view, as the review list does. -->
      <div v-if="hasMore || loading" ref="more" class="client-changes__more">
        <BasePreloader v-if="loading" color="purple" size="s"/>
      </div>
    </div>

    <template #footer>
      <p class="client-changes__note">{{ $t('carriers.import.clientChanges.Note') }}</p>
      <BaseButton type="outline" @click="$emit('close')">
        {{ $t('paymentImportHistory.close') }}
      </BaseButton>
      <BaseButton type="outline" :disabled="!total" :loading="downloading" @click="exportCsv">
        {{ $t('paymentImportHistory.exportCsv') }}
      </BaseButton>
    </template>
  </BaseSideModal>
</template>

<script>
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import InputSearch from '@/views/RenovaDashboard/components/ui-kit/Inputs/InputSearch';
import { apiError, formatCount } from '@/utils/carrierImport';

const PAGE = 100;

/**
 * Every client one carrier read touched, one row per person: made or recognised
 * (and how), the name as the carrier prints it where it differs, the RFC put
 * right, and the policies attached -- so a colectivo of 192 members accounts for
 * 192 people, not for the 183 the read did something surprising to. The client
 * counterpart of PaymentImportRunModal. A record only, so no undo: a wrong match
 * is fixed with the client merge / unmerge.
 */
export default {
  name: 'ClientChangesRunModal',
  components: {
    BaseSideModal, BaseButton, BasePreloader, InputSearch,
  },
  props: {
    scrapeJobId: { type: String, required: true },
  },
  emits: ['close'],
  data() {
    return {
      items: [],
      total: 0,
      counts: null,
      searchQuery: '',
      loading: true,
      loadError: '',
      downloading: false,
      observer: null,
      searchTimer: null,
      /**
       * Which fetch() is current. Typing bumps it at once, so an answer for the old search --
       * or a page the scroll asked for before the debounce fired -- can never land on the list.
       */
      seq: 0,
      /** A search typed and not yet sent: the scroll must not page the old list meanwhile. */
      searchPending: false,
    };
  },
  computed: {
    hasMore() {
      return this.items.length < this.total;
    },
  },
  created() {
    this.fetch(true);
  },
  mounted() {
    this.observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && this.hasMore && !this.loading && !this.searchPending) this.fetch(false);
    });
    this.$watch('hasMore', () => { this.$nextTick(() => this.observe()); }, { immediate: true });
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    clearTimeout(this.searchTimer);
  },
  methods: {
    formatCount,
    observe() {
      if (!this.observer) return;
      this.observer.disconnect();
      if (this.$refs.more) this.observer.observe(this.$refs.more);
    },
    nameDiffers(c) {
      if (!c.nameAfter) return false;
      const norm = (v) => (v || '').trim().toLowerCase().replace(/\s+/g, ' ');
      return norm(c.nameAfter) !== norm(c.clientName) || (c.nameBefore && norm(c.nameBefore) !== norm(c.nameAfter));
    },
    search(q) {
      this.searchQuery = q;
      // Whatever is in flight belongs to the old search; and no paging until the new one is sent.
      this.seq += 1;
      this.searchPending = true;
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.searchPending = false;
        this.fetch(true);
      }, 300);
    },
    async fetch(reset) {
      if (reset) { this.items = []; this.total = 0; }
      this.seq += 1;
      const {seq} = this;
      this.loading = true;
      this.loadError = '';
      try {
        const res = await this.$store.dispatch('carrierImportFetchClientChanges', {
          scrapeJobId: this.scrapeJobId,
          searchQuery: this.searchQuery || undefined,
          limit: PAGE,
          offset: reset ? 0 : this.items.length,
        });
        // A newer fetch -- another search, or a reset -- owns the list now.
        if (seq !== this.seq) return;
        this.items = reset ? (res.items || []) : this.items.concat(res.items || []);
        this.total = res.total || 0;
        this.counts = res.counts || null;
      } catch (e) {
        if (seq !== this.seq) return;
        this.loadError = apiError(e, this.$t('carriers.import.clientChanges.LoadFailed'));
      } finally {
        // Only the current fetch may say loading has stopped, or the scroll starts a third.
        if (seq === this.seq) {
          this.loading = false;
          this.$nextTick(() => this.observe());
        }
      }
    },
    async exportCsv() {
      this.downloading = true;
      try {
        const blob = await this.$store.dispatch('carrierImportExportClientChanges', {
          scrapeJobId: this.scrapeJobId,
          searchQuery: this.searchQuery || undefined,
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `carrier_clients_${this.scrapeJobId.slice(0, 8)}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.loadError = apiError(e, this.$t('carriers.import.clientChanges.LoadFailed'));
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.client-changes__search {
  width: 320px !important;
}

.client-changes__table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    text-align: left;
    vertical-align: top;
    padding: 6px 8px;
    border-bottom: 1px solid var(--solid-lavender-grey);
  }

  th {
    color: var(--dark-purple);
    font-weight: 600;
  }
}

.client-changes__new {
  color: var(--solid-purple);
  font-weight: 600;
}

.client-changes__policy {
  white-space: nowrap;
}

.client-changes__more {
  min-height: 32px;
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.client-changes__note {
  margin-right: auto;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--solid-dark-purple);
}
</style>
